import { __values, __awaiter, __generator, __assign } from 'tslib';
import { isObservableArray, observable, isObservableMap, action, autorun } from 'mobx';

/*!
 *
 * Copyright 2017 - acrazing
 *
 * @author acrazing joking.young@gmail.com
 * @since 2017-12-15 12:29:38
 * @version 1.0.0
 * @desc consts.ts
 */
var noop = function () { return void 0; };

/*!
 *
 * Copyright 2018 - acrazing
 *
 * @author acrazing joking.young@gmail.com
 * @since 2018-01-06 12=23=01
 * @version 1.0.0
 * @desc constants.ts
 */
var KeyVersions = '__mobx_sync_versions__';
var KeyDefaultKey = '__mobx_sync__';
var KeyActionName = '__PERSIST__';
var KeyNodeVersion = '__mobx_sync_this__';
var KeyIgnores = '__mobx_ignores__';
var KeyFormat = '__mobx_sync_format__';
var KeyInject = '__mobx_sync_inject__';

/*!
 * Copyright 2018 acrazing <joking.young@gmail.com>. All rights reserved.
 * @since 2018-06-27 00:21:42
 */
function isPrimitive(value) {
    if (value === void 0 || value === null) {
        return true;
    }
    var type = typeof value;
    return type === 'string' || type === 'number' || type === 'boolean';
}
function toJSON(data, recursive) {
    if (recursive === void 0) { recursive = true; }
    if (recursive) {
        var str = JSON.stringify(data);
        if (str === void 0) {
            return void 0;
        }
        return JSON.parse(str);
    }
    if (!data || !('toJSON' in data)) {
        return data;
    }
    return data.toJSON();
}
// TODO support es5 browsers
function parseCycle(input, map, prefix) {
    var e_1, _a;
    if (map === void 0) { map = new Map(); }
    if (prefix === void 0) { prefix = ''; }
    if (isPrimitive(input)) {
        return [];
    }
    if (!map.has(input)) {
        map.set(input, [prefix || '.']);
    }
    try {
        for (var _b = __values(Object.entries(input)), _c = _b.next(); !_c.done; _c = _b.next()) {
            var item = _c.value;
            if (isPrimitive(item[1]) || Object.keys(item[1]).length === 0) {
                continue;
            }
            var subPrefix = prefix + '.' + item[0];
            if (!map.has(item[1])) {
                map.set(item[1], [subPrefix]);
                parseCycle(item[1], map, subPrefix);
            }
            else {
                map.get(item[1]).push(subPrefix);
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_1) throw e_1.error; }
    }
    if (prefix !== '') {
        return [];
    }
    var output = [];
    map.forEach(function (value, key) {
        if (value.length > 1) {
            output.push([key, value]);
        }
    });
    return output;
}

/*!
 *
 * Copyright 2018 - acrazing
 *
 * @author acrazing joking.young@gmail.com
 * @since 2018-01-06 12:24:06
 * @version 1.0.0
 * @desc parse-store.ts
 */
var parseStore = function (store, data, isFromServer) {
    // if store or data is empty, break it
    if (!store || !data) {
        return;
    }
    var dataVersions = data[KeyVersions] || {};
    var storeVersions = store[KeyVersions] || {};
    var deserializers = store[KeyFormat] || {};
    // version control for node
    if ((KeyNodeVersion in dataVersions)
        || (KeyNodeVersion in storeVersions)) {
        if (dataVersions[KeyNodeVersion]
            !== storeVersions[KeyNodeVersion]) {
            return;
        }
    }
    // use data to iterate for avoid store does not set default value, and then
    // the properties will not exist actually. so, the observable
    // map/array/object field must has a default value, when the object is
    // constructed.
    for (var key in data) {
        // skip internal fields
        if (key === KeyVersions) {
            continue;
        }
        if (data.hasOwnProperty(key)) {
            // the version control for a field
            if (storeVersions[key] !== dataVersions[key]) {
                continue;
            }
            // if the new version of the store skipped a field, will
            // not assign stored data to it. this method need to the
            // store init the field with a value.
            var desc = Object.getOwnPropertyDescriptor(store, key);
            if (desc && !desc.enumerable && !isFromServer) {
                continue;
            }
            var storeValue = store[key];
            var dataValue = data[key];
            if (deserializers[key] && deserializers[key].deserializer) {
                store[key] = deserializers[key].deserializer(dataValue, storeValue);
            }
            else if (isObservableArray(storeValue)) {
                // mobx array
                store[key] = observable.array(dataValue);
            }
            else if (isObservableMap(storeValue)) {
                // mobx map
                store[key] = observable.map(dataValue);
            }
            else if (isPrimitive(dataValue)) {
                // js/mobx primitive objects
                store[key] = dataValue;
            }
            else if (!storeValue) {
                // if store value is empty, assign persisted data to it directly
                store[key] = dataValue;
            }
            else {
                // nested pure js object or mobx observable object
                parseStore(storeValue, dataValue, isFromServer);
            }
        }
    }
};
parseStore = action(parseStore);

/*!
 *
 * Copyright 2017 - acrazing
 *
 * @author acrazing joking.young@gmail.com
 * @since 2017-11-28 17:31:44
 * @version 1.0.0
 * @desc async.ts
 */
var AsyncTrunk = /** @class */ (function () {
    function AsyncTrunk(store, _a) {
        var _b = _a === void 0 ? {} : _a, _c = _b.storage, storage = _c === void 0 ? localStorage : _c, _d = _b.storageKey, storageKey = _d === void 0 ? KeyDefaultKey : _d, _e = _b.delay, delay = _e === void 0 ? 0 : _e, _f = _b.onError, onError = _f === void 0 ? noop : _f;
        this.store = store;
        this.storage = storage;
        this.storageKey = storageKey;
        this.delay = delay;
        this.onError = onError;
    }
    AsyncTrunk.prototype.persist = function () {
        return __awaiter(this, void 0, void 0, function () {
            var reason_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.storage.setItem(this.storageKey, JSON.stringify(this.store))];
                    case 1:
                        _a.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        reason_1 = _a.sent();
                        this.onError(reason_1);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * init the trunk async
     */
    AsyncTrunk.prototype.init = function (initialState) {
        return __awaiter(this, void 0, void 0, function () {
            var data, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.storage.getItem(this.storageKey)];
                    case 1:
                        data = _b.sent();
                        if (data) {
                            parseStore(this.store, JSON.parse(data), false);
                        }
                        return [3 /*break*/, 3];
                    case 2:
                        _a = _b.sent();
                        return [3 /*break*/, 3];
                    case 3:
                        if (initialState) {
                            parseStore(this.store, initialState, true);
                        }
                        // persist before listen change
                        this.persist();
                        this.disposer = autorun(this.persist.bind(this), {
                            name: KeyActionName,
                            delay: this.delay,
                            onError: this.onError,
                        });
                        return [2 /*return*/];
                }
            });
        });
    };
    AsyncTrunk.prototype.clear = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.storage.removeItem(this.storageKey)];
            });
        });
    };
    AsyncTrunk.prototype.updateStore = function (store) {
        this.store = store;
        return this.persist();
    };
    return AsyncTrunk;
}());

/*!
 * Copyright 2018 acrazing <joking.young@gmail.com>. All rights reserved.
 * @since 2018-12-15 15:35:02
 */
var options = {
    /**
     * is ssr or not, this only need to be set as true at server side.
     */
    ssr: false,
};
/**
 * update the configuration
 * @param input
 */
function config(input) {
    __assign(options, input);
}

/*!
 * Copyright 2018 acrazing <joking.young@gmail.com>. All rights reserved.
 * @since 2018-06-27 00:25:58
 */
function inject(target, key) {
    if (key !== void 0 && !target.hasOwnProperty(key)) {
        Object.defineProperty(target, key, {
            enumerable: false, value: Object.create(target[key] || null),
        });
    }
    if (target.hasOwnProperty(KeyInject)) {
        return;
    }
    Object.defineProperty(target, KeyInject, {
        value: true,
        configurable: false,
        enumerable: false,
    });
    var toJSON = target.toJSON;
    target.toJSON = function () {
        var data = toJSON ? toJSON.call(this) || {} : this;
        if (this[KeyFormat]) {
            var dump = {};
            for (var key_1 in data) {
                if (data.hasOwnProperty(key_1)
                    && this[KeyFormat][key_1]
                    && this[KeyFormat][key_1].serializer) {
                    dump[key_1] = this[KeyFormat][key_1].serializer(data[key_1]);
                }
                else {
                    dump[key_1] = data[key_1];
                }
            }
            data = dump;
        }
        if (this[KeyIgnores]) {
            var dump = {};
            for (var key_2 in data) {
                if (data.hasOwnProperty(key_2) && !this[KeyIgnores][key_2]) {
                    dump[key_2] = data[key_2];
                }
            }
            data = dump;
        }
        data[KeyVersions] = target[KeyVersions];
        return data;
    };
}

/*!
 * Copyright 2018 acrazing <joking.young@gmail.com>. All rights reserved.
 * @since 2018-06-27 00:25:38
 */
/**
 * define a custom stringify/parse function for a field, it is useful for
 * builtin objects, just like Date, TypedArray, etc.
 *
 * @example
 *
 * // this example shows how to format a date to timestamp,
 * // and load it from serialized string,
 * // if the date is invalid, will not persist it.
 * class SomeStore {
 *   @format<Date, number>(
 *      (timestamp) => new Date(timestamp),
 *      (date) => date ? +date : void 0,
 *   )
 *   dateField = new Date()
 * }
 *
 * @param deserializer - the function to parse the serialized data to
 *      custom object, the first argument is the data serialized by
 *      `serializer`, and the second is the current value of the field.
 * @param serializer - the function to serialize the object to pure js
 *      object or any else could be stringify safely by `JSON.stringify`.
 */
function format(deserializer, serializer) {
    return function (target, propertyKey) {
        inject(target, KeyFormat);
        target[KeyFormat][propertyKey] = { deserializer: deserializer, serializer: serializer };
    };
}
/**
 * The short hand for format date to ISO string
 *
 * @example
 *
 * class FooStore {
 *   @date
 *   dateField = new Date()
 * }
 */
var date = format(function (value) { return new Date(value); });
/**
 * the short hand for format RegExp
 */
var regexp = format(function (value) { return new RegExp(value.source, value.flags); }, function (value) { return ({ flags: value.flags, source: value.source }); });
function _ignore(target, propertyKey) {
    inject(target, KeyIgnores);
    target[KeyIgnores][propertyKey] = true;
}
/**
 * ignore the field, which means: if serialize the current store, it will
 * be omitted, and if the previous version does not omit it, will also
 * be omitted when call `parseStore`.
 *
 * Note: if set current runtime as ssr, will do nothing.
 *
 * @example
 *
 * class FooStore {
 *   @ignore
 *   bigTable = observable.map()
 * }
 *
 * @param target
 * @param propertyKey
 */
function ignore(target, propertyKey) {
    if (options.ssr) {
        return;
    }
    _ignore(target, propertyKey);
}
/**
 * same to `ignore`, but ignore the field even if the runtime is ssr.
 */
ignore.ssr = _ignore;
ignore.ssrOnly = function (target, propertyKey) {
    if (options.ssr) {
        _ignore(target, propertyKey);
    }
};
/**
 * set the version of the field, if the persisted data's version does not
 * equal to the current version, it will be omitted.
 *
 * @example
 *
 * class FooStore {
 *   // this means the current version of users struct is 1, if
 *   // your users's struct updated with breaking changes, you may need
 *   // to update the 1 to 2 to avoid loading the previous version's
 *   // users from localStorage.
 *   @version(1)
 *   users = observable.map()
 * }
 *
 * @param value - the version number, this should be different from all the
 *    old version when update it, a best practice is use q increment number.
 */
function version(value) {
    return function (target, key) {
        if (key === void 0) { key = KeyNodeVersion; }
        if (typeof target === 'function') {
            target = target.prototype;
        }
        inject(target);
        if (!target.hasOwnProperty(KeyVersions)) {
            target[KeyVersions] = __assign({}, target[KeyVersions] || {});
        }
        target[KeyVersions][key] = value;
    };
}

/*!
 *
 * Copyright 2017 - acrazing
 *
 * @author acrazing joking.young@gmail.com
 * @since 2017-11-28 17:31:44
 * @version 1.0.0
 * @desc sync.ts
 */
var SyncTrunk = /** @class */ (function () {
    function SyncTrunk(store, _a) {
        var _b = _a === void 0 ? {} : _a, _c = _b.storage, storage = _c === void 0 ? localStorage : _c, _d = _b.storageKey, storageKey = _d === void 0 ? KeyDefaultKey : _d, _e = _b.delay, delay = _e === void 0 ? 0 : _e, _f = _b.onError, onError = _f === void 0 ? noop : _f;
        this.store = store;
        this.storage = storage;
        this.storageKey = storageKey;
        this.delay = delay;
        this.onError = onError;
    }
    SyncTrunk.prototype.persist = function () {
        try {
            this.storage.setItem(this.storageKey, JSON.stringify(this.store));
        }
        catch (error) {
            this.onError(error);
        }
    };
    /**
     * init the store
     */
    SyncTrunk.prototype.init = function (initialState) {
        try {
            var data = this.storage.getItem(this.storageKey);
            if (data) {
                parseStore(this.store, JSON.parse(data), false);
            }
        }
        catch (_a) {
            // DO nothing
        }
        if (initialState) {
            parseStore(this.store, initialState, true);
        }
        // persist before listen change
        this.persist();
        this.disposer = autorun(this.persist.bind(this), {
            name: KeyActionName,
            delay: this.delay,
            onError: this.onError,
        });
    };
    SyncTrunk.prototype.clear = function () {
        this.storage.removeItem(this.storageKey);
    };
    SyncTrunk.prototype.updateStore = function (store) {
        this.store = store;
        this.persist();
    };
    return SyncTrunk;
}());

export { AsyncTrunk, KeyActionName, KeyDefaultKey, KeyFormat, KeyIgnores, KeyInject, KeyNodeVersion, KeyVersions, SyncTrunk, config, date, format, ignore, inject, isPrimitive, options, parseCycle, parseStore, regexp, toJSON, version };
